import { Token, TokenAmount } from 'config/types'
import useDebounceCallback from 'hooks/useDebounceCallback'
import { AppSearch } from 'layout/Components/GameSearch/styled'
import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useUserSettings } from 'state/profile/hooks'
import styled from 'styled-components'
import { Icons } from 'svgs'
import theme from 'theme'
import AlchemyModal, { ModalBody, ModalHeader, ModalProps } from 'UIKit/AlchemyModal'
import { ModalSizeModeEnum } from 'UIKit/AlchemyModal/Modal'
import Box from 'UIKit/Box/Box'
import Text from 'UIKit/Text'

import { MOBILE_BALANCE_MODAL_MAX_HEIGHT } from '../../constant'
import { useBalanceOptions } from '../../hooks/useBalanceOptions'
import { useBalanceSearch } from '../../hooks/useBalanceSearch'
import { useMobileBalanceAction } from '../../hooks/useMobileBalanceAction'
import TokenAmountOptionItem, { BalanceOptionItemProps } from '../TokenAmountOptionItem'

export interface MobileBalanceSelectMobileProps {
  title?: string
  handleSelectBalance: (token: TokenAmount) => void
  selectedToken: Token
  maxHeight?: any
  dismissAfterSelect?: boolean
  enableSearch?: boolean
  enableSetting?: boolean
}

const MobileBalanceSelectModal: React.FC<ModalProps<MobileBalanceSelectMobileProps>> = ({
  onDismiss,
  data: {
    title,
    selectedToken,
    handleSelectBalance,
    maxHeight = MOBILE_BALANCE_MODAL_MAX_HEIGHT,
    dismissAfterSelect = true,
    enableSearch = false,
    enableSetting = true,
  },
}) => {
  const { t } = useTranslation()
  const debounce = useDebounceCallback()
  const { userSettings } = useUserSettings()
  const { displayBalanceOptions } = useBalanceOptions(selectedToken)

  const { handlePresentMobileBalanceAction } = useMobileBalanceAction()

  const {
    onChange: handleSearchStringChange,
    filteredDisplayOptions,
    typedValue,
  } = useBalanceSearch(displayBalanceOptions)

  const handleClick = (item: BalanceOptionItemProps) => {
    if (!item.disabled) {
      handleSelectBalance(item.balance)

      if (dismissAfterSelect) {
        onDismiss()
      }
    }
  }

  const handleValueChanged = useCallback(
    (value: string) => {
      debounce(() => {
        handleSearchStringChange(value)
      }, 100)
    },
    [handleSearchStringChange, debounce],
  )

  return (
    <AlchemyModal
      onDismiss={onDismiss}
      colorTheme="darkblue"
      sizeMode={ModalSizeModeEnum.Auto}
      maxHeight={maxHeight}
      height={maxHeight}
    >
      <ModalHeader
        titleAlign="center"
        colorTheme="darkblue"
        useBackIcon={false}
        title={title}
        prefixNode={
          enableSetting && (
            <Box width="20px" height="20px" position="relative">
              <Icons.BalanceFilterIcon
                width={20}
                height={20}
                style={{ cursor: 'pointer' }}
                color={theme.colors.text_primary}
              />
              <Box
                position="absolute"
                width="200%"
                height="200%"
                left="50%"
                top="50%"
                style={{ transform: 'translate(-50%, -50%)' }}
                onClick={() => handlePresentMobileBalanceAction()}
              />
            </Box>
          )
        }
      />

      <ModalBody gap="12px" overflowY="hidden">
        {enableSearch && (
          <AppSearch
            autoFocus={false}
            placeholder={'Search Coins'}
            onValueChanged={handleValueChanged}
            minHeight={44}
          />
        )}

        <StyledOptionsContainer>
          {(() => {
            const filtered = userSettings.isHideZeroBalance
              ? filteredDisplayOptions.filter(
                  (item) => item.balance.amount.gt(0) || item.bonus.gt(0) || item.balance.token === selectedToken,
                )
              : filteredDisplayOptions

            return filtered.length > 0 ? (
              filtered.map((item, idx) => (
                <StyledOptionItem
                  key={item.balance.token.code + idx}
                  $disabled={item.disabled}
                  onClick={() => handleClick(item)}
                  disabled={item.disabled}
                >
                  <TokenAmountOptionItem item={item} />
                </StyledOptionItem>
              ))
            ) : (
              <Box padding="32px 0">
                <Text textAlign="center" color="text_tertiary">
                  {typedValue ? t('No Currency match your search') : t('No Currency available')}
                </Text>
              </Box>
            )
          })()}
        </StyledOptionsContainer>
      </ModalBody>
    </AlchemyModal>
  )
}

const StyledOptionsContainer = styled(Box)`
  overflow-y: auto;
  width: 100%;
`

const StyledOptionItem = styled.button<{ $disabled?: boolean }>`
  cursor: pointer;
  background: ${({ $disabled, theme }) => ($disabled ? theme.colors.card_primary_active_alt : 'transparent')};
  display: block;
  width: 100%;
  border-radius: ${({ theme }) => theme.radii.button};
  padding: 12px;
  outline: none;
  border: none;
  margin-bottom: 4px;

  &:hover {
    background-color: ${({ theme }) => theme.colors.card_primary_active_alt} !important;
  }

  &:focus-visible {
    background-color: ${({ theme }) => theme.colors.card_primary_active_alt};
  }

  &:disabled {
    background-color: ${({ theme }) => theme.colors.card_primary_active_alt} !important;
    border: 1px solid ${({ theme }) => theme.colors.primary};
  }
`

export default MobileBalanceSelectModal
